import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'
import { SplitFactoryProvider } from '@splitsoftware/splitio-react'
import _ from 'lodash'

import { api } from '@/app/services/api'
import { useLazyGetContactQuery, useLazyGetCustomerQuery } from '@/app/services/customer'
import { useLazyGetStoreSettingsQuery } from '@/app/services/organisation'
import { useGetOrCreateUserContextMutation } from '@/app/services/user'
import {
    selectAppIsLoading,
    selectCurrentUser,
    setAppIsLoading,
    setLanguage,
    setLocale,
    setUserRole,
} from '@/app/slices/appSlice'
import {
    selectOrganisation,
    selectOrganisationId,
    selectPlanMetadata,
    setOrganisation,
    setOrganisationId,
} from '@/app/slices/organisationSlice'
import { PageSpinner } from '@/common/components'
import { useAccessToken } from '@/common/hooks/useAccessToken'
import { useCustomDomain } from '@/common/hooks/useCustomDomain'
import useEffectOnlyOnUpdate from '@/common/hooks/useEffectOnlyOnUpdate'
import {
    getLanguageToUse,
    getLocaleToUse,
    initUserFlow,
    ORDER_USER_TYPE,
    Paths,
    setPageTitleAndFavicon,
} from '@/common/utils'
import Layout from '@/layout/Layout'

const UserRequiredContainer = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const appInsights = useAppInsightsContext()
    const { isLoadingOrgId, isSuccessOrgId } = useCustomDomain()

    const { i18n } = useTranslation()

    const { isAuthenticated, isLoading, user } = useKindeAuth()

    const { acquireToken } = useAccessToken()

    const organisationId = useSelector(selectOrganisationId)
    const appIsLoading = useSelector(selectAppIsLoading)
    const organisation = useSelector(selectOrganisation)
    const currentUser = useSelector(selectCurrentUser)
    const planMetadata = useSelector(selectPlanMetadata)

    const [splitConfig, setSplitConfig] = useState({
        core: {
            authorizationKey: import.meta.env.VITE_SPLIT_APIKEY,
            key: 'anonymous',
        },
    })

    const [getStoreSettings] = useLazyGetStoreSettingsQuery()
    const [getOrCreateUserContext] = useGetOrCreateUserContextMutation()
    const [getCustomer] = useLazyGetCustomerQuery()
    const [getContact] = useLazyGetContactQuery()

    const trackEvent = ({ data, eventName }) => appInsights.trackEvent({ name: eventName, properties: data })

    const initSplit = useCallback(
        (organisationId) => {
            setSplitConfig((prevConfig) => ({
                ...prevConfig,
                core: {
                    ...prevConfig.core,
                    key: organisationId || 'anonymous',
                    trafficType: 'organisation',
                },
            }))
        },
        [setSplitConfig]
    )

    const determineRedirectPath = (currentPath) => {
        if (currentPath === '/') {
            return Paths.HOME_PATHNAME.replace(':organisationId', organisationId)
        }
        return null
    }

    const initUserAndOrg = useCallback(async () => {
        let appUser = null
        let organisationResult = null

        try {
            organisationResult = await getStoreSettings({ organisationId }).unwrap()
            const title = organisationResult?.webStoreDisplayName || organisationResult?.organisationName
            setPageTitleAndFavicon(title, organisationResult?.webStoreFaviconUri)
        } catch (error) {
            if ([400, 401, 404].includes(error.status) && location.pathname !== Paths.NOT_FOUND) {
                console.error('Organisation not found, redirecting to not found page')

                trackEvent({
                    eventName: `Organisation not found, redirected to ${Paths.NOT_FOUND}`,
                    data: { currentUser: appUser, organisation: organisationResult },
                })

                navigate(Paths.NOT_FOUND)
                dispatch(setAppIsLoading(false))
                return
            }

            dispatch(api.util.resetApiState())

            return
        }

        if (isAuthenticated && user && (!currentUser || _.isEmpty(currentUser))) {
            await acquireToken()

            let userContext
            try {
                userContext = await getOrCreateUserContext({
                    organisationId,
                    user: { firstName: user?.given_name, lastName: user?.family_name, emailAddress: user?.email },
                }).unwrap()

                await getCustomer({ customerId: userContext?.customerId, organisationId })
                await getContact({
                    customerId: userContext?.customerId,
                    contactId: userContext?.contactId,
                    organisationId,
                })
            } catch (error) {
                userContext = error
            }

            if (!userContext?.user) {
                trackEvent({
                    eventName: 'User not found, redirected to error page',
                    data: { user, userContext },
                })
                navigate(Paths.NOT_FOUND)
                dispatch(setAppIsLoading(false))
                return
            }

            appUser = userContext.user
        }

        const role = appUser ? ORDER_USER_TYPE.Customer : ORDER_USER_TYPE.Guest
        const localeToUse = getLocaleToUse(organisationResult)
        const languageToUse = getLanguageToUse(appUser, organisationResult)

        i18n.changeLanguage(languageToUse)
        dispatch(setOrganisation(organisationResult))
        dispatch(setOrganisationId(organisationId))
        dispatch(setUserRole(role))
        dispatch(setLanguage(languageToUse))
        dispatch(setLocale(localeToUse))
        dispatch(setAppIsLoading(false))

        initSplit(organisationId)

        initUserFlow({
            userId: appUser?.userId || `guest_${organisationId}`,
            organisation: organisationResult,
            organisationId,
            language: languageToUse,
            planMetadata,
        })

        const redirectTo = determineRedirectPath(location.pathname)

        if (redirectTo) {
            trackEvent({
                eventName: `User redirected to ${redirectTo}`,
                data: { currentUser: appUser, organisation: organisationResult },
            })
            navigate(redirectTo)
        } else {
            trackEvent({
                eventName: 'User not redirected',
                data: { currentUser: appUser, organisation: organisationResult },
            })
        }
    }, [navigate, location, currentUser, user, planMetadata, trackEvent])

    useEffect(() => {
        if (!isLoading && !isLoadingOrgId && isSuccessOrgId && organisationId) {
            initUserAndOrg()
        }
    }, [isLoading, isSuccessOrgId, isLoadingOrgId, organisationId])

    useEffectOnlyOnUpdate(() => {
        if (organisation && currentUser) {
            const languageToUse = getLanguageToUse(currentUser, organisation)
            initUserFlow({
                userId: currentUser?.userId || `guest_${organisationId}`,
                organisation,
                organisationId,
                language: languageToUse,
                planMetadata,
            })
        }
    }, [organisation, currentUser, isAuthenticated, location.pathname])

    return appIsLoading ? (
        <PageSpinner />
    ) : (
        <SplitFactoryProvider config={splitConfig}>
            <Layout>
                <Outlet />
            </Layout>
        </SplitFactoryProvider>
    )
}

UserRequiredContainer.propTypes = {}

export default UserRequiredContainer
